import { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui';

/**
 * Payment-Link Link-Tester — Copy-Feld + QR (Spec 501, Phase 6).
 *
 * Der QR wird clientseitig als Inline-SVG erzeugt (`qrcode.react`) — KEIN externer
 * Request (CSP; Payment-Links sind Mobile-Links, der natuerlichste Test ist das
 * eigene Handy). Nur aktiv, wenn ein vollstaendiger, signierter Link existiert;
 * im Teaser/Prod-Platzhalter ist Copy gesperrt (`enabled=false`).
 */
export interface CopyAndQrProps {
  url: string;
  enabled: boolean;
}

export function CopyAndQr({ url, enabled }: CopyAndQrProps) {
  const { t } = useTranslation('payment-link');
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard nicht verfuegbar — kein harter Fehler fuer ein Test-Werkzeug.
    }
  }

  if (!enabled) {
    return (
      <p className="text-xs text-muted-foreground" data-testid="copyqr-disabled">
        {t('tester.copyqr.disabled')}
      </p>
    );
  }

  return (
    <div className="space-y-3" data-testid="copyqr">
      <div className="flex items-center gap-2">
        <code className="flex-1 break-all rounded bg-muted p-2 font-mono text-xs">
          {url}
        </code>
        <Button
          type="button"
          variant="outline"
          size="icon"
          aria-label={t('tester.copyqr.copy')}
          onClick={copy}
        >
          {copied ? (
            <Check className="h-4 w-4" aria-hidden="true" />
          ) : (
            <Copy className="h-4 w-4" aria-hidden="true" />
          )}
        </Button>
      </div>
      <QRCodeSVG value={url} size={128} aria-label={t('tester.copyqr.qrLabel')} />
    </div>
  );
}
